import _ from 'lodash';
import type { PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import { LICENSE_STATUS } from '@/licenseManagement/licenseManagement.constants.ts';
import { ValidityEnum } from '@/sdk/model/LicenseStatusOutputV1';

/*
 * This list is for convenience within Webserver to reference known licensed features. It is not strictly
 * necessary to add to this list, you can refer to the feature by string (instead of this enum).
 * The master list is the "salesforce_to_seeq_mapping" variable in licensing.py.
 */
export enum Feature {
  ODATA_Realtime_Export,
  Data_Lab,
  WITSML_Connector,
  AF_Data_Reference,
  Audit_Trail,
  Seeq_Connector_Plus_PI_EF,
  Asset_Group,
  Marketplace_6001,
  Marketplace_6002,
  Marketplace_6003,
  Marketplace_6004,
  Marketplace_6005,
  Marketplace_6006,
  Marketplace_6007,
  Marketplace_6008,
  Marketplace_6009,
  Marketplace_6010,
  Seeq_Preview,
  ThirdParty_DataSharing,
  Vantage,
  Locking,
}

export interface LicensedFeature {
  name: string;
  validity: ValidityEnum | string;
  daysToExpiration?: number;
  validThrough?: string;
}

export interface LicenseConfigurationEntry {
  'Service URL'?: string;
  'Access Key'?: string;
  [field: string]: string | undefined;
}

export interface LicenseConfiguration {
  'Seeq Usage'?: LicenseConfigurationEntry;
  [section: string]: LicenseConfigurationEntry | undefined;
}

export interface LicenseData {
  level?: string;
  companyName?: string;
  additionalFeatures?: LicensedFeature[];
  userLimitRestrict?: number;
  userCount?: number;
  contractNumber?: string;
  hostname?: string;
  validity?: ValidityEnum | string;
  configuration?: LicenseConfiguration;
  daysToExpiration?: number;
  userLimitWarn?: number;
  validThrough?: string;
}

export type LicenseStatus = typeof LICENSE_STATUS[keyof typeof LICENSE_STATUS];

export class LicenseManagementStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  static readonly storeName = 'sqLicenseManagementStore';

  initialize(): void {
    const initialLicense: LicenseData = {
      additionalFeatures: [],
    };

    this.state = this.immutable({
      licenseStatus: undefined as LicenseStatus | undefined,
      license: initialLicense,
      showOfficeHours: false,
    });
  }

  get licenseStatus(): LicenseStatus | undefined {
    return this.state.get('licenseStatus');
  }

  get license(): LicenseData {
    return this.state.get('license');
  }

  get showOfficeHours(): boolean {
    return this.state.get('showOfficeHours');
  }

  get isTrial(): boolean {
    return _.get(this.state.get('license'), 'level') === 'Trial';
  }

  hasValidFeature(feature: Feature): boolean {
    return (this.license.additionalFeatures ?? []).some((licensedFeature) => {
      const validity = licensedFeature.validity;
      return licensedFeature.name === Feature[feature] && (validity === ValidityEnum.Valid || validity === 'Valid');
    });
  }

  getFeature(name: string): LicensedFeature | undefined {
    return (this.license.additionalFeatures ?? []).find(
      (licensedFeature) => licensedFeature.name.toLowerCase() === name.toLowerCase(),
    );
  }

  get displayLicenseWarning(): boolean {
    const warningLevels = [LICENSE_STATUS.SHOW_ADMIN_LICENSE_WARNING, LICENSE_STATUS.SHOW_LICENSE_WARNING];
    return _.includes(warningLevels, this.state.get('licenseStatus'));
  }

  canAddUsers(): boolean {
    const { userLimitRestrict, userCount } = this.license;
    return !userLimitRestrict || (userCount ?? 0) < userLimitRestrict;
  }

  hasAssetGroups(): boolean {
    return (this.license.additionalFeatures ?? []).some((licensedFeature) => {
      const validity = licensedFeature.validity;
      return (
        licensedFeature.name === Feature[Feature.Asset_Group] &&
        (validity === ValidityEnum.Valid || validity === 'Valid')
      );
    });
  }

  protected readonly handlers = {
    SET_SHOW_OFFICE_HOURS: ({ showOfficeHours }: { showOfficeHours: boolean }) => {
      this.state.set('showOfficeHours', showOfficeHours);
    },
    LICENSE_SET_LICENSE: (license: LicenseData) => this.state.set('license', license),
    LICENSE_SET_STATUS: ({ status }: { status: LicenseStatus }) => this.state.set('licenseStatus', status),
  };
}
